import { useCallback, useEffect, useState } from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useAction, useTrouble } from "../App";
import { api, botName } from "../api/client";
import type { DiffView, LogLineView, RunView, StepView } from "../api/types";
import {
  Apparatus,
  Diff,
  Empty,
  Figure,
  LogLines,
  StatusPill,
} from "../components/Parts";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { saysSomethingNew } from "../lib/steps";
import { stopIsNew, stoppedEarly } from "../lib/stops";
import {
  between,
  grouped,
  now as rightNow,
  percent,
  short,
  since,
} from "../lib/format";

/** What the bots have done, most recent first. */
export function Runs() {
  const [runs, setRuns] = useState<RunView[] | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  // Which runs have been asked to stop. Cancelling returns as soon as the worker has been told,
  // but the worker still has to unwind, so re-reading immediately gets the status from before the
  // ask -- the card redraws unchanged and the button looks like it did nothing, which is the whole
  // failure this screen exists not to repeat.
  const [asked, setAsked] = useState<ReadonlySet<number>>(() => new Set());
  const report = useTrouble();

  const asking = useCallback(
    (id: number) => setAsked((it) => new Set(it).add(id)),
    [],
  );

  // Still waiting on something we asked to stop, so ask again quickly until it lands.
  const settling = (runs ?? []).some(
    (run) => asked.has(run.id) && stoppable(run),
  );

  useEffect(() => {
    let live = true;
    const load = () =>
      api
        .runs()
        .then((it) => live && setRuns(it))
        .catch(report);

    void load();
    const timer = window.setInterval(
      () => void load(),
      settling ? 1_000 : 10_000,
    );
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [report, settling]);

  if (!runs) return <Empty>Reading what has run.</Empty>;

  return (
    <>
      <h1 className="font-serif text-title font-medium -tracking-[0.015em]">
        Runs
      </h1>
      {runs.length === 0 ? (
        <Empty>Nothing has run yet.</Empty>
      ) : (
        runs.map((run) => (
          <Card key={run.id}>
            <CardContent>
              <Collapsible
                open={open === run.id}
                onOpenChange={(shown) => setOpen(shown ? run.id : null)}
                className="flex flex-col gap-3"
              >
                {/* Siblings, not nested: Stop inside the trigger would be a button inside a button,
                  which is invalid and leaves one of them unreachable by keyboard. */}
                <div className="flex items-center gap-3">
                  <CollapsibleTrigger className="flex flex-1 cursor-pointer items-center gap-3 text-left">
                    <Summary run={run} />
                  </CollapsibleTrigger>
                  {stoppable(run) ? (
                    <StopRun
                      run={run}
                      asked={asked.has(run.id)}
                      onAsked={asking}
                    />
                  ) : null}
                </div>
                <CollapsibleContent>
                  <Detail run={run} />
                </CollapsibleContent>
              </Collapsible>
            </CardContent>
          </Card>
        ))
      )}
    </>
  );
}

/**
 * Whether there is anything to stop.
 *
 * The server only cancels a run a worker is holding, and answers 409 for any other. Those are the
 * two statuses that means, so drawing the button on a finished run would offer an action that can
 * only ever refuse.
 */
function stoppable(run: RunView): boolean {
  return run.status === "RUNNING" || run.status === "WAITING_APPROVAL";
}

function StopRun({
  run,
  asked,
  onAsked,
}: {
  run: RunView;
  asked: boolean;
  onAsked: (id: number) => void;
}) {
  const [sending, act] = useAction();
  const stopping = sending || asked;

  return (
    <Button
      variant="destructive"
      disabled={stopping}
      onClick={() =>
        act(async () => {
          await api.cancel(run.id);
          onAsked(run.id);
        })
      }
    >
      {stopping ? "Stopping" : "Stop"}
    </Button>
  );
}

function Summary({ run }: { run: RunView }) {
  const moment = rightNow();
  return (
    <>
      <StatusPill status={run.status} />
      <span className="text-heading font-medium">{botName(run)}</span>
      <span className="font-mono text-mono text-muted-foreground">
        {run.taskName ?? "one-off"}
      </span>
      <span className="flex-1" />
      <span className="font-mono text-mono text-muted-foreground">
        {grouped(run.lookedAt ?? 0)} looked at · {grouped(run.changed ?? 0)}{" "}
        changed
        {run.notAttempted > 0
          ? ` · ${grouped(run.notAttempted)} not attempted`
          : ""}
      </span>
      <span className="font-mono text-mono text-muted-foreground">
        {since(run.finishedAt ?? run.queuedAt, moment)}
      </span>
    </>
  );
}

/**
 * One run in full: what it did, how long it took, and everything it said.
 *
 * The whole log rather than a tail. A finished run is read to find out why it did what it did, and
 * the line that explains it is as likely to be the first as the last.
 */
function Detail({ run }: { run: RunView }) {
  const [lines, setLines] = useState<LogLineView[]>([]);
  const [sample, setSample] = useState<DiffView | null>(null);
  const [steps, setSteps] = useState<StepView[]>([]);
  const report = useTrouble();

  useEffect(() => {
    let live = true;
    api
      .log(run.id, 0)
      .then((it) => live && setLines(it))
      .catch(report);
    // `null` here means the run recorded no diff: it changed nothing a diff can show. A failure
    // throws, so the two are no longer the same thing on screen.
    api
      .diff(run.id)
      .then((it) => live && setSample(it))
      .catch(report);
    // The row came from the list, which carries no steps. This is where "which language failed" is
    // answered, so it is worth the second request once somebody opens the run.
    api
      .run(run.id)
      .then((it) => live && setSteps(it.steps ?? []))
      .catch(report);
    return () => {
      live = false;
    };
  }, [run.id, report]);

  const lookedAt = run.lookedAt ?? 0;
  const saved = run.saved ?? 0;
  const errors = run.errors ?? 0;
  const stopped = stoppedEarly(run);

  return (
    <div className="flex flex-col gap-3">
      <div className="font-mono text-mono text-muted-foreground">
        run {grouped(run.id)} ·{" "}
        {run.source === "SCHEDULE" ? "scheduled" : "by hand"} ·{" "}
        {run.taskName ?? "one-off"} · took {took(run)}
      </div>

      <div className="flex flex-wrap gap-6 *:flex-1">
        <Figure value={grouped(lookedAt)} label="Looked at" />
        <Figure
          value={grouped(run.changed ?? 0)}
          label={`Changed ${percent(run.changed ?? 0, lookedAt)}`}
        />
        <Figure
          value={grouped(saved)}
          label="Saved"
          tone={saved > 0 ? "running" : "muted"}
        />
        <Figure
          value={grouped(errors)}
          label="Errors"
          tone={errors > 0 ? "failed" : "muted"}
        />
        {/* Counted apart from looked at: a run stopped with pages in hand is not one that finished. */}
        <Figure
          value={grouped(run.notAttempted)}
          label="Not attempted"
          tone={run.notAttempted > 0 ? "waiting" : "muted"}
        />
        <Figure
          value={run.rate ? `${grouped(run.rate)}/s` : "—"}
          label="Mean rate"
        />
      </div>

      {stopped ? (
        <p className="m-0 max-w-[60ch] text-row text-pretty text-waiting">
          {stopped}
        </p>
      ) : null}

      {run.error ? (
        <p
          className={`m-0 max-w-[60ch] text-row text-pretty ${
            run.status === "DECLINED"
              ? "text-muted-foreground"
              : "text-destructive"
          }`}
        >
          {run.error}
        </p>
      ) : null}

      <Steps steps={steps} said={run.error} stoppedFor={run.stopReason} />

      <div className="flex items-start gap-8 max-sm:flex-col max-sm:gap-6">
        {lines.length > 0 ? (
          <div className="flex min-w-0 flex-1 flex-col gap-2.5">
            <Apparatus>Log — {grouped(lines.length)} lines, durable</Apparatus>
            <LogLines lines={lines} />
          </div>
        ) : null}

        {sample ? (
          <div className="flex min-w-0 flex-1 flex-col gap-2.5">
            <Apparatus>
              Sample diff, {sample.saved ? "saved" : "dry run"} — {sample.title}
            </Apparatus>
            <Diff diff={sample.diff} />
            {sample.truncated ? (
              <p className="m-0 text-row text-muted-foreground">
                Shortened. The whole one is in logs/run-{run.id}/diffs.log.
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** How far one level of nesting shifts a step, in pixels off the 4px base. */
const INDENT = 16;

/**
 * What the run was made of, in the order it did them.
 *
 * The whole point of keeping steps: a run over thirty-six languages that ends with one number cannot say
 * which language did the work, and a language that failed looks exactly like one that found nothing. Here
 * each one says what it got through, and the one that failed or stopped says why — once, where it happened,
 * rather than again on every step around it and on the run.
 */
function Steps({
  steps,
  said,
  stoppedFor,
}: {
  steps: StepView[];
  said?: string | null;
  stoppedFor: string | null;
}) {
  if (steps.length === 0) return null;

  // How far in each one sits. Worked out from the chain rather than assumed, so the list reads correctly
  // however deep a bot nests — the depth is the bot's business, not this screen's.
  const byId = new Map(steps.map((step) => [step.id, step]));
  const depthOf = (step: StepView): number => {
    let depth = 0;
    let above = step.parent == null ? undefined : byId.get(step.parent);
    while (above !== undefined) {
      depth++;
      above = above.parent == null ? undefined : byId.get(above.parent);
    }
    return depth;
  };

  return (
    <div className="flex flex-col gap-3">
      <Apparatus>Steps — {grouped(steps.length)}</Apparatus>
      {steps.map((step) => {
        const lookedAt = step.lookedAt ?? 0;
        const total = step.total ?? 0;

        return (
          <div
            key={step.id}
            className={`flex flex-col gap-1${step.error ? " border-l-2 border-l-destructive pl-2" : ""}`}
            style={{ marginLeft: depthOf(step) * INDENT }}
          >
            <div className="flex items-baseline gap-2 text-row text-muted-foreground">
              <span className="text-foreground">{step.name}</span>
              <span className="flex-1" />
              <span className="font-mono text-mono">
                {grouped(lookedAt)} looked at · {grouped(step.changed ?? 0)}{" "}
                changed · {grouped(step.saved ?? 0)} saved
                {step.notAttempted > 0
                  ? ` · ${grouped(step.notAttempted)} not attempted`
                  : ""}
              </span>
            </div>
            <Progress
              value={total > 0 ? Math.min(100, (lookedAt / total) * 100) : 100}
              indicatorClassName={
                step.error ? "bg-destructive" : "bg-muted-foreground"
              }
            />
            {saysSomethingNew(step, steps, said) ? (
              <span className="text-row text-destructive">{step.error}</span>
            ) : null}
            {stopIsNew(step, steps, stoppedFor) ? (
              <span className="text-row text-waiting">
                Stopped: {step.stopReason}
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function took(run: RunView): string {
  if (!run.startedAt || !run.finishedAt) return "—";
  return short(between(run.startedAt, run.finishedAt));
}
